import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { resolveNotificationUrl } from '@/lib/notificationDeepLinks';
import { scopedChannel } from '@/lib/realtime';
import { localizeNotification } from '@/lib/notificationI18n';
import i18n from 'i18next';

export interface HubNotification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  data: Record<string, any>;
  is_read: boolean;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  category: string;
  action_url?: string;
  action_text?: string;
  created_at: string;
}

export interface GroupedNotification {
  key: string;
  category: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  notifications: HubNotification[];
  latestAt: string;
  isGrouped: boolean;
  // Merged display
  title: string;
  message: string;
  action_url?: string;
  hasUnread: boolean;
}

type TabFilter = 'all' | 'bookings' | 'offers' | 'messages' | 'system';

const PRIORITY_ORDER: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 };

const CATEGORY_MAP: Record<string, TabFilter> = {
  booking: 'bookings',
  offer: 'offers',
  message: 'messages',
  general: 'all',
  system: 'system',
  trip: 'bookings',
  payment: 'bookings',
  dispute: 'system',
};

function getTabForCategory(category: string): TabFilter {
  return CATEGORY_MAP[category] || 'all';
}

function groupKey(n: HubNotification): string {
  // Group by type + related entity (from data)
  const entityId = n.data?.booking_id || n.data?.request_id || n.data?.trip_id || n.data?.conversation_id || '';
  // Messages from same sender
  if (n.type === 'question_received' || n.type === 'answer_received' || n.type === 'new_message') {
    const sender = n.data?.sender_id || n.data?.courier_name || n.data?.customer_name || '';
    return `msg:${sender}`;
  }
  // Shipment updates group by booking
  if (n.type?.startsWith('shipment_') || n.type?.startsWith('delivery_')) {
    return `shipment:${entityId}`;
  }
  // Offers group together
  if (n.type === 'new_bid' || n.type === 'match_offer_new') {
    return `offer:${n.data?.request_id || 'general'}`;
  }
  // Default: no grouping (unique key)
  return `single:${n.id}`;
}

function mergeGroup(group: HubNotification[]): GroupedNotification {
  const latest = group[0];
  const hasUnread = group.some(n => !n.is_read);
  const highestPriority = group.reduce((best, n) => {
    return (PRIORITY_ORDER[n.priority] ?? 3) < (PRIORITY_ORDER[best] ?? 3) ? n.priority : best;
  }, 'low' as HubNotification['priority']);

  if (group.length === 1) {
    return {
      key: groupKey(latest),
      category: latest.category,
      priority: latest.priority,
      notifications: group,
      latestAt: latest.created_at,
      isGrouped: false,
      title: latest.title,
      message: latest.message,
      action_url: latest.action_url,
      hasUnread,
    };
  }

  // Merge titles for grouped notifications. These strings are user-visible in
  // the header bell, so they must be translated like the rest of the portal.
  const tr = i18n.t.bind(i18n) as (k: string, o?: any) => string;
  let title = latest.title;
  let message = latest.message;

  if (latest.type === 'question_received' || latest.type === 'answer_received' || latest.type === 'new_message') {
    const senderName = latest.data?.courier_name || latest.data?.customer_name
      || tr('notif.group.someone', { defaultValue: 'Someone' });
    title = tr('notif.group.messages', { defaultValue: '{{name}} sent {{count}} messages', name: senderName, count: group.length });
    message = latest.message;
  } else if (latest.type === 'new_bid' || latest.type === 'match_offer_new') {
    title = tr('notif.group.offers', { defaultValue: '{{count}} new offers received', count: group.length });
    message = tr('notif.group.latest', { defaultValue: 'Latest: {{message}}', message: latest.message });
  } else if (latest.type?.startsWith('shipment_') || latest.type?.startsWith('delivery_')) {
    title = tr('notif.group.shipment', { defaultValue: 'Shipment update' });
    message = tr('notif.group.updates', { defaultValue: '{{count}} updates – Latest: {{message}}', count: group.length, message: latest.message });
  } else {
    title = `${group.length} × ${latest.title}`;
  }


  return {
    key: groupKey(latest),
    category: latest.category,
    priority: highestPriority,
    notifications: group,
    latestAt: latest.created_at,
    isGrouped: true,
    title,
    message,
    action_url: latest.action_url,
    hasUnread,
  };
}

const PAGE_SIZE = 50;

function mapRow(n: any): HubNotification {
  return {
    id: n.id,
    user_id: n.user_id,
    type: n.type,
    title: n.title,
    message: n.message,
    data: n.data || {},
    is_read: n.read || false,
    priority: n.priority || 'normal',
    category: n.category || 'general',
    action_url: n.action_url,
    action_text: n.action_text,
    created_at: n.created_at,
  };
}

export function useNotificationHub() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<HubNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [isOpen, setIsOpen] = useState(false);
  const mountedRef = useRef(true);

  const userId = user?.id;

  // Pass the real role through so deep-link resolution can route admin /
  // support notifications correctly (e.g. /support-portal/live-chat,
  // /admin/couriers/:id) instead of always landing on /account/notifications.
  const userRole = user?.role || 'customer';

  // Fetch notifications. A failed fetch MUST surface as an error, never as an
  // empty inbox — "you have no notifications" is a dangerous lie when the
  // request actually failed.
  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    const [page, unread] = await Promise.all([
      supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE),
      supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('read', false),
    ]);
    if (!mountedRef.current) return;
    if (page.error) {
      console.error('Failed to fetch notifications:', page.error);
      setLoadError(page.error.message);
      setIsLoading(false);
      return;
    }
    setLoadError(null);
    const rows = page.data || [];
    setNotifications(rows.map(mapRow));
    setHasMore(rows.length === PAGE_SIZE);
    setTotalUnread(unread.count ?? rows.filter((r: any) => !r.read).length);
    setIsLoading(false);
  }, [userId]);

  const loadMore = useCallback(async () => {
    if (!userId || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const oldest = notifications[notifications.length - 1]?.created_at;
    let q = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(PAGE_SIZE);
    if (oldest) q = q.lt('created_at', oldest);
    const { data, error } = await q;
    if (!mountedRef.current) return;
    if (error) {
      setLoadError(error.message);
    } else {
      const rows = data || [];
      setNotifications((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...rows.map(mapRow).filter((n) => !seen.has(n.id))];
      });
      setHasMore(rows.length === PAGE_SIZE);
    }
    setIsLoadingMore(false);
  }, [userId, notifications, hasMore, isLoadingMore]);


  useEffect(() => {
    mountedRef.current = true;
    fetchNotifications();

    if (!userId) return;

    // With the boot flag OFF we keep the legacy per-user channel name. With it
    // ON we route through the scoped helper so `realtime_can_subscribe`
    // enforces user-id ownership.
    const channel = scopedChannel(
      { kind: 'user', id: userId },
      'notifications'
    )
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef.current) return;
        const mapped = mapRow({ ...(payload.new as any), read: false });
        setNotifications(prev => prev.some(n => n.id === mapped.id) ? prev : [mapped, ...prev]);
        setTotalUnread(prev => prev + 1);

        // Show in-app toast for all new notifications
        const duration = mapped.priority === 'urgent' ? 12000 : mapped.priority === 'high' ? 8000 : 5000;
        const deepUrl = resolveNotificationUrl(mapped, userRole);
        const loc = localizeNotification(mapped as any, i18n.t.bind(i18n) as any);
        toast(loc.title || mapped.title, {
          description: loc.message || mapped.message,
          action: {
            label: loc.actionText || mapped.action_text || i18n.t('notif.action.viewDetails', { defaultValue: 'View' }),
            onClick: () => { window.location.href = deepUrl; },
          },
          duration,
        });
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef.current) return;
        const updated = payload.new as any;
        setNotifications(prev =>
          prev.map(n => n.id === updated.id ? { ...n, is_read: updated.read || false } : n)
        );
      })
      .subscribe();

    return () => {
      mountedRef.current = false;
      // MUST use removeChannel — plain unsubscribe() leaves the channel in
      // the client registry, so the next mount (StrictMode double-invoke,
      // route change, auth refresh) reuses the cached SUBSCRIBED instance
      // and .on() throws "cannot add postgres_changes callbacks after
      // subscribe()", which crashes login/portal via the error boundary.
      supabase.removeChannel(channel);
    };
  }, [userId, fetchNotifications]);

  // Computed values. The badge must reflect ALL unread rows, not just the
  // first page held in memory, otherwise it under-reports after 50 items.
  const loadedUnread = useMemo(() => notifications.filter(n => !n.is_read).length, [notifications]);
  const unreadCount = Math.max(loadedUnread, totalUnread);


  // Sync badge count to service worker and App Badge API
  useEffect(() => {
    // Try direct App Badge API first (works on some browsers without SW)
    if ('setAppBadge' in navigator) {
      if (unreadCount > 0) {
        (navigator as any).setAppBadge(unreadCount).catch(() => {});
      } else {
        (navigator as any).clearAppBadge?.().catch(() => {});
      }
    }
    // Also tell the service worker so it stays in sync
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SET_BADGE_COUNT',
        count: unreadCount,
      });
    }
  }, [unreadCount]);

  const grouped = useMemo(() => {
    // Filter by tab
    let filtered = notifications;
    if (activeTab !== 'all') {
      filtered = notifications.filter(n => {
        const tab = getTabForCategory(n.category);
        if (activeTab === 'bookings') return tab === 'bookings';
        if (activeTab === 'offers') return tab === 'offers';
        if (activeTab === 'messages') return tab === 'messages';
        if (activeTab === 'system') return tab === 'system';
        return true;
      });
    }

    // Group
    const groups = new Map<string, HubNotification[]>();
    for (const n of filtered) {
      const key = groupKey(n);
      const existing = groups.get(key) || [];
      existing.push(n);
      groups.set(key, existing);
    }

    // Merge and sort by priority then date
    const merged = Array.from(groups.values()).map(mergeGroup);
    merged.sort((a, b) => {
      // Unread first
      if (a.hasUnread !== b.hasUnread) return a.hasUnread ? -1 : 1;
      // Then priority
      const pa = PRIORITY_ORDER[a.priority] ?? 3;
      const pb = PRIORITY_ORDER[b.priority] ?? 3;
      if (pa !== pb) return pa - pb;
      // Then date
      return new Date(b.latestAt).getTime() - new Date(a.latestAt).getTime();
    });

    return merged;
  }, [notifications, activeTab]);

  const tabCounts = useMemo(() => ({
    all: notifications.filter(n => !n.is_read).length,
    bookings: notifications.filter(n => !n.is_read && getTabForCategory(n.category) === 'bookings').length,
    offers: notifications.filter(n => !n.is_read && getTabForCategory(n.category) === 'offers').length,
    messages: notifications.filter(n => !n.is_read && getTabForCategory(n.category) === 'messages').length,
    system: notifications.filter(n => !n.is_read && getTabForCategory(n.category) === 'system').length,
  }), [notifications]);

  // Optimistic writes are rolled back when the database rejects them —
  // otherwise a failed update looks read until the next page load.
  const markAsRead = useCallback(async (notificationId: string) => {
    setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
    setTotalUnread(prev => Math.max(0, prev - 1));
    const { error } = await supabase.from('notifications').update({ read: true } as any).eq('id', notificationId);
    if (error) {
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: false } : n));
      setTotalUnread(prev => prev + 1);
      toast.error(i18n.t('notif.markReadFailed', { defaultValue: 'Could not update this notification.' }));
    }
  }, []);

  const markGroupAsRead = useCallback(async (group: GroupedNotification) => {
    const unreadIds = group.notifications.filter(n => !n.is_read).map(n => n.id);
    if (unreadIds.length === 0) return;
    setNotifications(prev => prev.map(n => unreadIds.includes(n.id) ? { ...n, is_read: true } : n));
    setTotalUnread(prev => Math.max(0, prev - unreadIds.length));
    const { error } = await supabase.from('notifications').update({ read: true } as any).in('id', unreadIds);
    if (error) {
      setNotifications(prev => prev.map(n => unreadIds.includes(n.id) ? { ...n, is_read: false } : n));
      setTotalUnread(prev => prev + unreadIds.length);
      toast.error(i18n.t('notif.markReadFailed', { defaultValue: 'Could not update this notification.' }));
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!userId) return;
    const snapshot = notifications;
    const previousUnread = totalUnread;
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setTotalUnread(0);
    const { error } = await supabase.rpc('mark_all_notifications_as_read');
    if (error) {
      setNotifications(snapshot);
      setTotalUnread(previousUnread);
      toast.error(i18n.t('notif.markReadFailed', { defaultValue: 'Could not update this notification.' }));
    }
  }, [userId, notifications, totalUnread]);

  const dismiss = useCallback(async (notificationId: string) => {
    const snapshot = notifications;
    const wasUnread = snapshot.find(n => n.id === notificationId && !n.is_read);
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (wasUnread) setTotalUnread(prev => Math.max(0, prev - 1));
    const { error } = await supabase.from('notifications').delete().eq('id', notificationId);
    if (error) {
      setNotifications(snapshot);
      if (wasUnread) setTotalUnread(prev => prev + 1);
      toast.error(i18n.t('notif.deleteFailed', { defaultValue: 'Could not delete this notification.' }));
    }
  }, [notifications]);

  return {
    notifications: grouped,
    rawNotifications: notifications,
    unreadCount,
    isLoading,
    loadError,
    hasMore,
    isLoadingMore,
    loadMore,
    activeTab,
    setActiveTab,
    tabCounts,
    markAsRead,
    markGroupAsRead,
    markAllAsRead,
    dismiss,
    isOpen,
    setIsOpen,
    refetch: fetchNotifications,
  };

}
